/* ──────────────────────────────────────────────
 *  Lignes de prestation du récap (spec Parcours § 9, ligne 1),
 *  communes au récap du tunnel, à la confirmation et à la page de
 *  gestion : libellé, rappel du nombre de personnes, formule et
 *  durée, prix. Tout arrive formaté serveur.
 * ────────────────────────────────────────────── */

import { PriceType } from '@cfixe-monorepo/shared';
import type { BookingLabels } from '../types';
import { priceOptionDisplay } from './price-display';

/** Rappel « · N pers. » d'une ligne : le nombre de personnes multiplie son prix côté serveur. */
export const partySizeSuffix = (partySize: number | undefined, template: string): string =>
  partySize !== undefined && partySize > 1 ? ` · ${template.replace('{count}', String(partySize))}` : '';

/**
 * Libellé d'une ligne réservée (confirmation, page de gestion) : rappel du nombre de personnes (unités facturées,
 * envoyées au-delà d'une seule), sinon de la quantité au-delà d'un exemplaire.
 */
export const bookedLineLabel = (
  line: { name: string; quantity: number; party_size?: number },
  partySizeTemplate: string,
): string =>
  line.party_size !== undefined && line.party_size > 1
    ? `${line.name}${partySizeSuffix(line.party_size, partySizeTemplate)}`
    : line.quantity > 1
      ? `${line.name} x${line.quantity}`
      : line.name;

/** Sous-ligne « formule · durée » (« Cheveux longs · 45 min ») ; rien quand le serveur n'en envoie pas. */
export const lineDetailSublines = (line: { formula?: string; duration?: string }): string[] => {
  const detail = [line.formula, line.duration].filter(Boolean).join(' · ');
  return detail ? [detail] : [];
};

/**
 * Prix affiché d'une ligne : forme courte « À déterminer » sans prix (une ligne vide à côté d'une ligne chiffrée
 * serait ambiguë), sinon le libellé de son type (« Sur devis », « À partir de … », « Gratuit », montant).
 */
export const recapLinePrice = (
  priceType: string | undefined,
  formattedPrice: string | undefined,
  labels: Pick<BookingLabels, 'price_to_determine_short' | 'price_from' | 'on_quote' | 'free'>,
): string =>
  priceType === PriceType.NONE
    ? labels.price_to_determine_short
    : priceOptionDisplay(priceType, formattedPrice ?? '', labels);
